import { useSyncExternalStore } from "react";

// The software keyboard, read off the visual viewport. Browsers disagree on
// what a keyboard does to the page — some shrink the window to fit above it,
// others keep the window and shrink only the visual viewport — so "open" is
// judged against the tallest visual viewport seen at this width. Only the
// visual viewport feeds that baseline: Chrome on Android reports a window
// height that briefly includes the keyboard mid-transition, which would
// poison it for good. `height` is the visible height: the edge the keyboard
// starts at, in rect coordinates.

export type ViewportSample = {
  height: number;
  width: number;
  // Pinch-zoom shrinks the visual viewport too; that is not a keyboard.
  scale: number;
};

export type KeyboardState = {
  open: boolean;
  height: number;
};

type Rest = { width: number; height: number };

// Smaller drops are browser chrome (the URL bar) coming and going.
export const KEYBOARD_MIN_DROP = 120;

export const CLOSED: KeyboardState = { open: false, height: 0 };

export const deriveKeyboard = (sample: ViewportSample, rest: Rest): { state: KeyboardState; rest: Rest } => {
  const sameWidth = Math.abs(sample.width - rest.width) < 1;
  const tallest = Math.max(sameWidth ? rest.height : 0, sample.height);
  const open = sample.scale <= 1.05 && tallest - sample.height >= KEYBOARD_MIN_DROP;
  return {
    state: open ? { open, height: Math.round(sample.height) } : CLOSED,
    rest: { width: sample.width, height: tallest },
  };
};

const readSample = (): ViewportSample | null => {
  if (typeof window === "undefined") return null;
  const vv = window.visualViewport;
  if (!vv) return null;
  return { height: vv.height, width: vv.width, scale: vv.scale || 1 };
};

let rest: Rest = { width: 0, height: 0 };
let state: KeyboardState = CLOSED;
const listeners = new Set<() => void>();

const measure = () => {
  const sample = readSample();
  if (!sample) return;
  const next = deriveKeyboard(sample, rest);
  rest = next.rest;
  if (next.state.open === state.open && next.state.height === state.height) return;
  state = next.state;
  listeners.forEach((l) => l());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  const vv = typeof window !== "undefined" ? window.visualViewport : null;
  if (listeners.size === 1 && vv) {
    vv.addEventListener("resize", measure);
    window.addEventListener("resize", measure);
    measure();
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && vv) {
      vv.removeEventListener("resize", measure);
      window.removeEventListener("resize", measure);
    }
  };
};

const getSnapshot = () => state;
const getServerSnapshot = () => CLOSED;

export const useKeyboard = (): KeyboardState => useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

// Tests swap the viewport under the store; the remembered rest height must go with it.
export const resetKeyboardStore = () => {
  rest = { width: 0, height: 0 };
  state = CLOSED;
};
